import { httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { environment } from '@rpgc-ai/environments';

import { TranslocoService } from '@jsverse/transloco';
import { map, timer } from 'rxjs';

import { NotificationsService } from '../notifications/notifications.service';

export interface IEmailVerificationResponse {
  success: true;
  sentAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmailVerificationService {
  readonly #window = inject(WINDOW);

  readonly #translationService = inject(TranslocoService);
  readonly #notificationsService = inject(NotificationsService);

  readonly #profileStore = inject(ProfileStore);

  readonly email = signal<string | null>(null);
  readonly emailSentTime = signal<string | null>(this.#window?.localStorage.getItem('emailSentAt') ?? null);
  readonly #dismissedMandatoryUrl = signal<string | null>(null);

  readonly #now = toSignal(timer(0, 1000).pipe(map(() => Date.now())), { initialValue: Date.now() });

  readonly secondsLeft = computed<number>(() => {
    const sentAt = this.emailSentTime();
    if (!sentAt) {
      return 0;
    }

    const diffMs = this.#now() - new Date(sentAt).getTime();
    return Math.max(60 - Math.floor(diffMs / 1000), 0);
  });

  readonly resource = httpResource<IEmailVerificationResponse>(() => {
    const email = this.email();

    if (!email || !email.length) {
      return;
    }

    return {
      url: `/profile/resend-verification-email`,
      method: 'POST',
      body: {
        email,
        frontendUrl: `https://${environment.APP_DOMAIN}`,
      },
    };
  });

  constructor() {
    this.#setEmailSentAtEffect();
  }

  markMandatoryModalOpened(): void {
    this.#dismissedMandatoryUrl.set(null);
  }

  markMandatoryModalDismissed(url: string): void {
    this.#dismissedMandatoryUrl.set(this.#normalizeUrl(url));
  }

  shouldOpenMandatoryModal(targetUrl: string, currentUrl: string): boolean {
    const normalizedTarget = this.#normalizeUrl(targetUrl);
    const normalizedCurrent = this.#normalizeUrl(currentUrl);

    return !(normalizedTarget === normalizedCurrent && this.#dismissedMandatoryUrl() === normalizedTarget);
  }

  #normalizeUrl(url: string): string {
    return url.split('#')[0] || '/';
  }

  /**
   * Установка времени отправки письма
   */
  #setEmailSentAtEffect(): void {
    effect(() => {
      const error = this.resource.error();
      const isLoading = this.resource.isLoading();
      const email = this.email();

      if (!isLoading && email && email.length) {
        untracked(() => {
          if (error) {
            const message = this.#translationService.translate('MODALS.PROFILE_SETTINGS.MY_ACCOUNT.EMAIL_VERIFICATION.ERROR', {
              error: error.message,
            });
            this.#notificationsService.addErrorNotification(message);
          } else {
            const value = this.resource.value();
            if (!value) {
              return;
            }
            //Сохраняем в localStorage
            this.#window?.localStorage.setItem('emailSentAt', value.sentAt);
            this.emailSentTime.set(value.sentAt);

            this.#profileStore.setEmail(email);
            this.email.set(null);
          }
        });
      }
    });
  }
}
